import React, { KeyboardEvent, PropsWithChildren, useMemo } from 'react'
import clsx from 'clsx'

import MenuButton from '../base/MenuButton'
import { SidebarConfig } from './useSidebar'
import './sidebar.scss'
import Button from '../base/Button'

export type SidebarProps = {
  config: SidebarConfig
  className?: string
}

const Sidebar: React.FC<PropsWithChildren<SidebarProps>> = ({ config, className, children }) => {
  // Only include tab items (non-button) for keyboard navigation
  const flatNames = useMemo(() => {
    const result: string[] = []
    config.groups.forEach(g =>
      g.items.forEach(i => {
        if (i.type !== 'button') result.push(i.name)
      })
    )
    return result
  }, [config.groups])

  const focusTabByName = (name: string) => {
    const ids = config.a11y[name]
    if (ids?.tabId) {
      const el = document.getElementById(ids.tabId)
      el?.focus()
    }
  }

  const handleKeyDown = (e: KeyboardEvent, currentName: string) => {
    const idx = flatNames.indexOf(currentName)
    if (idx === -1) return

    let nextIdx = idx
    switch (e.key) {
      case 'ArrowDown':
        nextIdx = (idx + 1) % flatNames.length
        break
      case 'ArrowUp':
        nextIdx = (idx - 1 + flatNames.length) % flatNames.length
        break
      case 'Home':
        nextIdx = 0
        break
      case 'End':
        nextIdx = flatNames.length - 1
        break
      default:
        return
    }
    e.preventDefault()
    const nextName = flatNames[nextIdx]
    if (nextName) {
      config.onClick(nextName)
      // Move focus to the newly active tab
      requestAnimationFrame(() => focusTabByName(nextName))
    }
  }

  return (
    <aside className={clsx('tp-sidebar', className)} aria-label={config.title}>
      <h2 className="tp-sidebar__title">{config.title}</h2>
      <nav className="tp-sidebar__groups" aria-label={`${config.title} navigation`}>
        {config.groups.map(grp => {
          const tabItems = grp.items.filter(i => i.type !== 'button')
          const buttonItems = grp.items.filter(i => i.type === 'button')
          return (
            <div key={grp.group} className="tp-sidebar__group">
              <label className="tp-sidebar__group-label">{grp.group}</label>
              {tabItems.length > 0 && (
                <div role="tablist" aria-orientation="vertical" className="tp-sidebar__tabs">
                  {tabItems.map(item => {
                    const ids = config.a11y[item.name]
                    const isActive = config.active === item.name
                    return (
                      <MenuButton
                        key={item.name}
                        active={isActive}
                        aria-selected={isActive}
                        role="tab"
                        id={ids?.tabId}
                        aria-controls={ids?.panelId}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => config.onClick(item.name)}
                        onKeyDown={e => handleKeyDown(e, item.name)}
                        icon={item.icon}
                      >
                        {item.label || item.name}
                      </MenuButton>
                    )
                  })}
                </div>
              )}
              {buttonItems.length > 0 && (
                <div className="tp-sidebar__actions">
                  {buttonItems.map(item => (
                    <Button
                      key={item.name}
                      size="small"
                      variant="neutral-primary"
                      icon={item.icon}
                      onClick={() => item.action && item.action()}
                    >
                      {item.name}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )
        })}
        {children && <div className="tp-sidebar__footer">{children}</div>}
      </nav>
    </aside>
  )
}

export default Sidebar
